<script setup lang="ts">
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";
import {BlockProps} from "@/utils/presenters/interfaces.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {
  addRefToList,
  commonCanReceiveFocus,
  commonFocus,
  commonNavigateToNext,
  NamaNavigateToNextEmit,
  NamaNavigationArgs,
  NamaVueWidgetContainer
} from "@/utils/common/NamaVueWidget.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import CompositeEditScreenField from "@/components/EditView/editScreens/CompositeEditScreenField.vue";
import EditScreenField from "@/components/EditView/editScreens/EditScreenField.vue";
import EditScreenGroup = namaTypes.EditScreenGroup;


const props = defineProps<BlockProps>();
let block = props.block as EditScreenGroup;


const availableFields: (namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder)[] | undefined =
  block.fields?.filter(l => props.presenter.isValidFieldForAttach(l));

let refList: NamaVueWidgetContainer[] = [];

function onRef(widget: any, index: number) {
  refList = addRefToList(refList, {widget, index});
}

const emit = defineEmits<NamaNavigateToNextEmit>()

function createStandardFieldFor(field: namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder) {
  if (ObjectChecker.areEqual(field['namaClassTypeId'], "CompositeEditScreenField"))
    return CompositeEditScreenField;
  return EditScreenField;
}

function onNavigateToNext(args: NamaNavigationArgs, index: number) {
  commonNavigateToNext(refList, args, index, () => emit('navigateToNext', args));
}

function fetchFieldType(field: (namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder)): string | null {
  let fieldMetaData = EditViewUtils.getFieldMetaData(props.presenter.metadata, field.id);
  return fieldMetaData && fieldMetaData.fieldType ? fieldMetaData.fieldType : null;
}

function isSpanned(field: namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder): boolean {
  return field.layout === EditScreenFieldLayout.spanned || field.layout === EditScreenFieldLayout.spanned2
}

function isSpanned2(field: namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder): boolean {
  if (field.layout !== EditScreenFieldLayout.spanned2)
    return false;
  return fetchFieldType(field) === FieldType.BigText;
}


function focus(args: NamaNavigationArgs) {
  commonFocus(refList, args, () => emit('navigateToNext', args));
}

function canReceiveFocus(): boolean {
  return commonCanReceiveFocus(refList);
}

defineExpose({focus, canReceiveFocus});
</script>

<template>
  <div v-if="availableFields && availableFields.length > 0"
       class="tw-flex tw-flex-wrap tw-gap-x-[4%] compact:tw-gap-x-[1%] ultra:tw-gap-x-[0.5%] quad:tw-gap-x-[1%] quad:compact:tw-gap-x-[0.5%] quad:ultra:tw-gap-x-[0.25%] horizontal:tw-gap-x-[0%] tw-items-center">
    <template v-for="(field, index) in availableFields" :key="index">
      <div v-if="field.layout === EditScreenFieldLayout.alone" class="tw-min-w-full"></div>
      <div data-input-field class="tw-mb-2 mobile:tw-min-w-[99%] compact:tw-mb-0.5 ultra:tw-mb-0"
           :class="{'tw-w-[48%] compact:tw-w-[49.5%] ultra:tw-w-[49.5%] quad:tw-w-[24%] quad:compact:tw-w-[24.5%] quad:ultra:tw-w-[24.5%] horizontal:tw-w-[50%] horizontal:quad:tw-w-[25%]':!isSpanned(field),
                   'tw-min-w-full quad:horizontal:tw-min-w-full quad:compact:tw-min-w-[99.5%] quad:ultra:tw-min-w-[99.5%] quad:tw-min-w-[99%]':(isSpanned(field) || isSpanned2(field)),
         'tw-min-h-[60px] horizontal:tw-min-h-[45px] horizontal:compact:tw-py-0.5 horizontal:ultra:tw-py-0.5 horizontal:compact:tw-min-h-fit horizontal:ultra:tw-min-h-fit compact:tw-min-h-fit ultra:tw-min-h-fit':!isSpanned2(field),
         'tw-min-h-[136px]':isSpanned2(field)}">
        <component :is="createStandardFieldFor(field)" :field="field" :isInsideComposite="false" :presenter="presenter"
                   :fieldMetaData="EditViewUtils.getFieldMetaData(presenter.metadata, field.id)!" :isSpanned="isSpanned(field)"
                   :ref="(r:any)=>onRef(r,index)" @navigateToNext="(args)=>onNavigateToNext(args,index)"/>
      </div>
      <div v-if="field.layout === EditScreenFieldLayout.alone" class="tw-min-w-full"></div>
    </template>
  </div>
</template>

<style scoped>

</style>
